<template>
  <VueFinalModal
    v-model="isOpen"
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-5xl h-[85vh] bg-[#fafbfc] rounded-[32px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <div class="flex w-full h-full min-h-0">
      <aside class="w-72 bg-[#1c4848] text-white p-8 flex flex-col justify-between relative overflow-hidden shrink-0">
        <div class="absolute inset-0 opacity-10 mix-blend-overlay pointer-events-none" style="background-image: url('https://grainy-gradients.vercel.app/noise.svg')" />
        <div class="relative z-10">
          <div class="flex items-center gap-3 mb-6 opacity-80">
            <div class="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[#ecd79f]">
              <IconCatchStar class="w-[18px] h-[18px]" />
            </div>
            <span class="text-xs font-bold tracking-[0.2em] uppercase">Playing</span>
          </div>
          <h3 class="font-serif text-3xl font-medium mb-1 tracking-tight text-[#f2f4f4]">
            {{ (gameLoaded || game)?.name || 'Catch the Star' }}
          </h3>
          <p class="text-xs text-white/50 font-medium uppercase tracking-wider mb-8">Catch Star</p>
          <div class="space-y-4">
            <div class="bg-white/5 rounded-2xl p-4 border border-white/5">
              <div class="flex items-center gap-2 text-[#ecd79f] mb-1">
                <span class="text-[10px] font-bold uppercase tracking-wider">Score</span>
              </div>
              <p class="text-3xl font-bold font-mono tracking-tight">{{ gameStats.score ?? 0 }}</p>
            </div>
            <div class="bg-white/5 rounded-2xl p-4 border border-white/5">
              <div class="flex items-center gap-2 text-[#ecd79f] mb-1">
                <span class="text-[10px] font-bold uppercase tracking-wider">Lives</span>
              </div>
              <p class="text-3xl font-bold font-mono tracking-tight">{{ gameStats.lives ?? 0 }}</p>
            </div>
            <div class="bg-white/5 rounded-2xl p-4 border border-white/5">
              <div class="flex items-center gap-2 text-[#ecd79f] mb-1">
                <span class="text-[10px] font-bold uppercase tracking-wider">Time</span>
              </div>
              <p class="text-3xl font-bold font-mono tracking-tight">{{ gameStats.timeLeft ?? 0 }}s</p>
            </div>
          </div>
        </div>
        <div class="relative z-10 space-y-3">
          <button type="button" class="w-full py-3.5 rounded-xl bg-[#ecd79f] text-[#1c4848] font-bold text-sm hover:bg-[#ffe082] transition-all flex items-center justify-center gap-2" @click="onRestart">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            Restart
          </button>
          <button type="button" class="w-full py-3.5 rounded-xl bg-white/5 text-white/60 font-bold text-sm hover:bg-white/10 hover:text-white transition-colors" @click="close">Exit</button>
        </div>
      </aside>
      <main class="flex-1 bg-[#f2f4f4] relative p-8 flex flex-col items-center justify-center overflow-y-auto min-h-0">
        <div v-if="!ready" class="text-[#1c4848]/50 text-sm">Loading…</div>
        <CatchStarPlay v-else-if="gameLoaded" ref="playRef" :game="gameLoaded" @stats="gameStats = $event" />
      </main>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import CatchStarPlay from '@/components/app/dashboard/games/play/CatchStarPlay.vue';
import IconCatchStar from '@/components/app/dashboard/games/icons/IconCatchStar.vue';
import { useGamesStore } from '@/store/games/index.js';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  game: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'close']);

const isOpen = computed({ get: () => props.modelValue, set: (v) => emit('update:modelValue', v) });
const gamesStore = useGamesStore();
const gameLoaded = ref(null);
const gameStats = ref({ score: 0, lives: 3, timeLeft: 60 });
const playRef = ref(null);

const ready = computed(() => Boolean((gameLoaded.value || props.game)?.id));

watch(() => [props.modelValue, props.game?.id], async () => {
  if (!props.modelValue || !props.game?.id) {
    gameLoaded.value = null;
    gameStats.value = { score: 0, lives: 3, timeLeft: 60 };
    return;
  }
  try {
    const loaded = await gamesStore.fetchOne(props.game.id);
    gameLoaded.value = loaded;
  } catch {
    gameLoaded.value = props.game;
  }
}, { immediate: true });

function close() {
  emit('update:modelValue', false);
  emit('close');
}

function onRestart() {
  playRef.value?.restart?.();
}
</script>
